import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Alert,
    Checkbox,
    Fab,
    IconButton,
    LinearProgress,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import StartMillingProductionDialog from './start-milling-production-dialog.component';
import { millingTasksTemplate } from 'apollo/templates/milling/query.graphql';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { MillingFilters, MillingJob, MillingJobCopyReason } from 'models/milling';
import StlMultiPreviewList from 'components/shared/stl-multi-preview-list/stl-multi-preview-list.component';

type MillingJobsTodoListProps = {
    filters: MillingFilters;
};
const MillingJobsTodoList: FC<MillingJobsTodoListProps> = ({ filters }: MillingJobsTodoListProps) => {
    const { t: translate } = useTranslation(['milling-jobs', 'orders']);
    const [startProductionOpen, setStartProductionOpen] = useState(false);
    const [millingJobs, setMillingJobs] = useState<MillingJob[]>([]);
    const [checkedJobs, setCheckedJobs] = useState<Map<string, CheckedJob>>(new Map());
    const [selectedJobs, setSelectedJobs] = useState<MillingJob[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    const [millingTasks, { loading, refetch }] = useLazyQuery(millingTasksTemplate, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            setMillingJobs(data.millingTasks);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        millingTasks({ variables: { filters: filters } });
    }, [filters]);

    useEffect(() => {
        const newCheckedJobs: Map<string, CheckedJob> = new Map();
        millingJobs.forEach((job) => {
            const id = job._id as string;
            newCheckedJobs.set(id, { job: job, isChecked: false });
        });
        setCheckedJobs(newCheckedJobs);
        setSelectedJobs([]);
    }, [millingJobs]);

    const handleCheckClick = (job: MillingJob, newValue: boolean) => {
        const newCheckedJobs = new Map(checkedJobs);
        newCheckedJobs.set(job._id as string, { job: job, isChecked: newValue });
        setCheckedJobs(newCheckedJobs);
    };

    const handleStartProductionClick = () => {
        setSelectedJobs(
            Array.from(checkedJobs.values())
                .filter((cj) => cj.isChecked)
                .map((cj) => cj.job),
        );
        setStartProductionOpen(true);
    };

    const isAnyJobsChecked = () => {
        return !Array.from(checkedJobs.values()).find((cj) => cj.isChecked);
    };

    const handleProductionStarted = () => {
        setStartProductionOpen(false);
        if (refetch) {
            refetch();
        }
    };

    const isCheckboxEnabled = (job: MillingJob) => {
        const firstChecked = Array.from(checkedJobs.values()).find((job) => job.isChecked);
        if (firstChecked) {
            return (
                firstChecked.job.jobType === job.jobType &&
                firstChecked.job.material === job.material &&
                firstChecked.job.color === job.color
            );
        }
        return true;
    };

    return (
        <Fragment>
            {loading && <LinearProgress color="secondary" />}
            <Fab
                sx={{ position: 'fixed', left: 100, bottom: 20, zIndex: 100 }}
                variant="extended"
                color="primary"
                onClick={() => handleStartProductionClick()}
                disabled={isAnyJobsChecked()}
            >
                {translate('milling-jobs:START_PRODUCTION_FOR_SELECTED')}
            </Fab>
            {!loading && millingJobs.length ? (
                <TableContainer component={Paper} sx={{ padding: 1, marginBottom: 5 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 50 }}></TableCell>
                                <TableCell sx={{ width: 380 }}>{translate('milling-jobs:JOB_GROUP')}</TableCell>
                                <TableCell>{translate('milling-jobs:DEADLINE')}</TableCell>
                                <TableCell>{translate('orders:COMMENT')}</TableCell>
                                <TableCell>{translate('orders:ORDER')}</TableCell>
                                <TableCell>{translate('orders:INTERNAL_ORDER_ID')}</TableCell>
                                <TableCell>{translate('common:CREATED')}</TableCell>
                                <TableCell sx={{ width: 160 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {millingJobs.map((job) => (
                                <TableRow key={job._id}>
                                    <TableCell>
                                        <Checkbox
                                            disabled={!isCheckboxEnabled(job)}
                                            checked={checkedJobs.get(job._id as string)?.isChecked || false}
                                            onChange={(_, val) => {
                                                handleCheckClick(job, val);
                                            }}
                                            sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {job.jobType}: {job.material} {job.color}{' '}
                                        {job.copyReason === MillingJobCopyReason.REDO && (
                                            <Typography color="warning.dark" variant="caption">
                                                ({translate('milling-jobs:REDO')})
                                            </Typography>
                                        )}
                                        {job.copyReason === MillingJobCopyReason.WARRANTY && (
                                            <Typography color="warning.dark" variant="caption">
                                                ({translate('milling-jobs:WARRANTY_JOB')})
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {job.deadline
                                            ? formatDate(job.deadline, 'date')
                                            : translate('milling-jobs:NO_DEADLINE')}
                                    </TableCell>
                                    {job.comment ? (
                                        <Tooltip title={job.comment} placement="top">
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        maxWidth: 200,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {job.comment}
                                                </Typography>
                                            </TableCell>
                                        </Tooltip>
                                    ) : (
                                        <TableCell>-</TableCell>
                                    )}
                                    <TableCell>
                                        <Link
                                            style={{ cursor: 'pointer' }}
                                            target="_blank"
                                            href={`/order-details/${job.order._id}/main-view`}
                                        >
                                            {job.order.orderId}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {job.order.internalOrderId
                                            ? job.order.internalOrderId
                                            : translate('orders:NO_INTERNAL_ORDER_ID')}
                                    </TableCell>
                                    <TableCell>{formatDate(job.createdAt, 'datetime')}</TableCell>
                                    <TableCell align="right">
                                        {job.stlFiles?.length > 0 && <StlMultiPreviewList stlFiles={job.stlFiles} />}
                                        <IconButton color="primary" href={job.zipFileKey} disabled={!job.zipFileKey}>
                                            <DownloadIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('milling-jobs:NO_MILLING_JOBS_TO_DO')}
                </Alert>
            )}

            <StartMillingProductionDialog
                jobs={selectedJobs}
                open={startProductionOpen}
                onClose={() => setStartProductionOpen(false)}
                onProductionStarted={handleProductionStarted}
            />
        </Fragment>
    );
};

type CheckedJob = {
    job: MillingJob;
    isChecked: boolean;
};

export default MillingJobsTodoList;
